import React from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  ActivityIndicator,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { PaymentModal, PaymentModalContent } from '@/components/residents/payments';
import { useAppSelector } from '@/store';
import { usePaymentProcessing } from '@/features/payments/hooks/usePaymentProcessing';

type Params = {
  leaseId: number;
  residentId?: number;
  amount?: number;
  sourceId?: number;
  onSuccess?: string; // Screen to navigate to on success
  onFailure?: string; // Screen to navigate to on failure
  onTimeout?: string; // Screen to navigate to on timeout
  paymentId?: string; // Optional: payment id for Yardi import/polling when available
};

export const TransactionProcessingScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  
  const { 
    leaseId, 
    residentId, 
    amount, 
    sourceId, 
    onSuccess = 'TransactionReceived',
    onFailure = 'PaymentSummaryModal',
    onTimeout = 'TransactionTimeout',
    paymentId,
  } = (route.params || {}) as Params;

  const { message, currentMessageIndex, totalSteps, progressPercent, isLoading } = usePaymentProcessing({
    leaseId,
    residentId,
    amount,
    sourceId,
    paymentId,
    onSuccess: ({ leaseId: lId, residentId: rId, amount: amt, paymentMethod }) => {
      navigation.replace(onSuccess, {
        leaseId: lId,
        residentId: rId,
        amount: amt,
        paymentMethod,
      });
    },
    onFailure: () => {
      navigation.replace(onFailure, { leaseId });
    },
    onTimeout: () => {
      navigation.replace(onTimeout, { leaseId });
    },
  });

  return (
    <PaymentModal title="Processing Payment" onClose={() => navigation.replace(onFailure, { leaseId })} width={480} disableOverlayClose>
      <PaymentModalContent>
        <View style={styles.content}>
          <ActivityIndicator 
            size="large" 
            color={RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY} 
            style={styles.spinner}
          />
          
          <Text style={styles.message}>
            {message}
          </Text>
          
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <View 
                style={[
                  styles.progressFill, 
                  { width: `${progressPercent}%` }
                ]} 
              />
            </View>
            <Text style={styles.progressText}>
              Step {currentMessageIndex + 1} of {totalSteps}
            </Text>
          </View>
          
          <Text style={styles.disclaimer}>
            Please do not close this window or navigate away while your payment is being processed.
          </Text>
        </View>
      </PaymentModalContent>
    </PaymentModal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  spinner: {
    marginBottom: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 16,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  progressContainer: {
    width: '100%',
    maxWidth: 300,
    marginBottom: 32,
  },
  progressBar: {
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
    borderRadius: 2,
  },
  progressText: {
    fontSize: 12,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
  },
  disclaimer: {
    fontSize: 12,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    fontStyle: 'italic',
    paddingHorizontal: 16,
  },
});

export default TransactionProcessingScreen;
